import React, { useState, forwardRef, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import './ImageUploader.scss';

import UploadImage from './UploadImage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faFileUpload } from '@fortawesome/pro-regular-svg-icons';
import DropUploader from './DropUploader';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/lib/ReactCrop.scss';
import 'react-image-crop/dist/ReactCrop.css';
import { cropImage } from '../api/images';
import Loader from '../components/General/Loader';
import { ImageUploaderInput } from './ImageUploaderInput';
import ImageCropper from './ImageCropper';
import cogoToast from 'cogo-toast';
import { getSmartImage } from '../helpers/helpers';

/**
 * This is a modal that allows for the user to upload an image or enter an image URL.
 * When they click save, whichever image option they have selected will be returned
 * in a callback function. In that callback function, you can define how you want to
 * handle the image.
 */

const ImageUploaderComponent = (props, ref) => {
  const { initialImageUrl, initialSourceUrl, isVisible, setIsVisible, onSaveCallback, previewAspectRatio, allowCrop, cropAspect, cropSize } = props;

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [image, setImage] = useState();
  // Re-cropping starts from the original photo, not the previously cropped result.
  const [url, setUrl] = useState(initialSourceUrl || initialImageUrl);
  const [crop, setCrop] = useState({});
  const [isCropping, setIsCropping] = useState(false);

  const hasImage = image || url;
  const isAbleToClear = !hasImage && initialImageUrl;
  const isDragging = useRef(false);
  const croppedAreaPixelsRef = useRef(null);
  const mediaSizeRef = useRef(null);
  const cropAdjustedRef = useRef(false);
  const useSquareCrop = allowCrop && !!cropAspect;

  const imageUnchanged = !image && url === (initialSourceUrl || initialImageUrl);

  useImperativeHandle(ref, () => ({
    getImageFile: () => image,
    getImageUrl: () => url
  }));

  const clearInputs = () => {
    if (isCropping) return;
    setImage('');
    setUrl('');
    setCrop({});
    croppedAreaPixelsRef.current = null;
    mediaSizeRef.current = null;
    cropAdjustedRef.current = false;
  };

  const saveAndExit = async () => {
    if (uploading || isCropping) return;

    // Nothing was re-framed and the image is the same one — closing must not re-crop.
    if (useSquareCrop && imageUnchanged && !cropAdjustedRef.current && initialImageUrl) {
      setIsVisible(false);
      return;
    }

    const rawUrl = image || url || '';
    const source_url = useSquareCrop ? getSmartImage(rawUrl) || rawUrl : rawUrl;
    let final_url = source_url;
    if (useSquareCrop) {
      const pixels = croppedAreaPixelsRef.current;
      if (pixels?.width && pixels?.height) {
        setIsCropping(true);
        try {
          const naturalWidth = mediaSizeRef.current?.naturalWidth;
          if (!naturalWidth) {
            throw new Error('Image is still loading. Wait a moment and try Save again.');
          }
          const { url: croppedUrl } = await cropImage(
            final_url,
            {
              x: pixels.x,
              y: pixels.y,
              width: pixels.width,
              height: pixels.height,
              frame: { width: naturalWidth }
            },
            { size: cropSize || 800, allow_extend: true }
          );
          final_url = croppedUrl;
        } catch (err) {
          window.ALERT.error(typeof err === 'string' ? err : err?.message || 'There was an error cropping your image.');
          setIsCropping(false);
          return;
        }
        setIsCropping(false);
      }
    } else if (crop.width && crop.height) {
      setIsCropping(true);
      const [cropFrame] = document.getElementsByClassName('ReactCrop');
      const { width, height } = cropFrame.getBoundingClientRect();
      try {
        const { url } = await cropImage(final_url, { ...crop, frame: { width, height } }, { size: cropSize });
        final_url = url;
      } catch {
        window.ALERT.error('There was an error uploading your image.');
      } finally {
        setIsCropping(false);
      }
    }

    await onSaveCallback(final_url, { sourceUrl: source_url });
    setImage('');
    setUrl('');
    setCrop({});
    croppedAreaPixelsRef.current = null;
    mediaSizeRef.current = null;
    setIsVisible(false);
  };

  const completeUpload = url => {
    setImage(getSmartImage(url));
    setCrop({});
    croppedAreaPixelsRef.current = null;
    mediaSizeRef.current = null;
    cropAdjustedRef.current = false;
    setUploading(false);
  };

  const onUploadProgress = progress => {
    setUploading(true);
    setUploadProgress(progress);
  };

  const handleFileUploaded = fileUrl => {
    setUrl(fileUrl);
    setCrop({});
    croppedAreaPixelsRef.current = null;
    mediaSizeRef.current = null;
    cropAdjustedRef.current = false;
    cogoToast.success('Image uploaded successfully.');
  };

  const handleDragDrop = (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length) return window.ALERT.error('Only image files are supported.');
    setUploading(false);
  };

  if (!isVisible) return null;
  return (
    <div
      className='image-uploader-outer'
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
        if (isDragging.current) return;
        setIsVisible(false);
      }}
    >
      <div
        className={`image-uploader-inner${useSquareCrop ? ' image-uploader-inner--square-crop' : ''}`}
        onClick={e => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <DropUploader onDrop={handleDragDrop} onUpload={handleFileUploaded} onlyAllowImages>
          <div className='heading'>
            <div className='image-uploader-header'>Enter An Image</div>
            <div className='image-uploader-sub-header'>Only .jpg, .jpeg, .png, and .webp files are supported.</div>
          </div>
          {/* <input type='file' accept='image/*' onChange={handleImageUpload} onClick={e => e.stopPropagation()} /> */}
          <UploadImage
            basicContent={
              image ? (
                <div className='upload-image-container'>
                  Upload Complete
                  <FontAwesomeIcon icon={faCheck} />
                </div>
              ) : (
                <div className='upload-image-container'>
                  Upload Image
                  <FontAwesomeIcon icon={faFileUpload} />
                  {uploading && <span> ({uploadProgress})</span>}
                </div>
              )
            }
            basicUploader={true}
            onUploadProgress={onUploadProgress}
            completeUpload={completeUpload}
            imagesOnly={true}
          />

          <ImageUploaderInput url={url} onUpload={handleFileUploaded} />

          {hasImage &&
            (useSquareCrop ? (
              <>
                <ImageCropper
                  imageSrc={getSmartImage(image || url)}
                  aspectRatio={cropAspect}
                  frameLabel={`${cropSize || 800} × ${cropSize || 800}`}
                  onMediaLoaded={size => {
                    mediaSizeRef.current = size;
                  }}
                  onCropComplete={pixels => {
                    croppedAreaPixelsRef.current = pixels;
                  }}
                  onAdjust={() => {
                    cropAdjustedRef.current = true;
                  }}
                />
                <p>
                  The bright square is exactly what will show, saved at {cropSize || 800} × {cropSize || 800}. The full photo starts inside it — zoom and
                  drag to tighten the crop; any empty space saves as white.
                </p>
              </>
            ) : allowCrop ? (
              <>
                <ReactCrop
                  src={image || url}
                  crop={crop}
                  onChange={c => setCrop(c)}
                  onDragStart={() => {
                    isDragging.current = true;
                  }}
                  onDragEnd={() => {
                    window.setTimeout(() => {
                      isDragging.current = false;
                    }, 0);
                  }}
                />
                <p>Drag on the image to apply a crop</p>
              </>
            ) : (
              <img
                src={getSmartImage(image || url)}
                alt='user input'
                style={{ aspectRatio: previewAspectRatio ? `${previewAspectRatio}` : 'unset' }}
              />
            ))}
          {(hasImage || isAbleToClear) && (
            <div className='buttons'>
              {hasImage ? (
                <button className='clear-button' disabled={isCropping} onClick={clearInputs}>
                  {isCropping ? <Loader size={40} /> : 'Clear'}
                </button>
              ) : null}
              <button className='save-button' disabled={isCropping} onClick={saveAndExit}>
                {isCropping ? <Loader size={40} /> : 'Save'}
              </button>
            </div>
          )}
        </DropUploader>
      </div>
    </div>
  );
};

const ImageUploader = forwardRef(ImageUploaderComponent);
ImageUploader.propTypes = {
  initialImageUrl: PropTypes.string,
  initialSourceUrl: PropTypes.string,
  isVisible: PropTypes.bool.isRequired,
  setIsVisible: PropTypes.func.isRequired,
  onSaveCallback: PropTypes.func.isRequired,
  previewAspectRatio: PropTypes.number,
  allowCrop: PropTypes.bool,
  cropAspect: PropTypes.number,
  cropSize: PropTypes.number
};
export default ImageUploader;
